import { InviteRow } from '@/types/adminInvites';

// MM-42243: the single source of truth for the bulk-invite grid's columns — their display order, friendly label, CSV
// header text, editor type, and which are required per row. The `header` strings MUST match the 23 CsvColumn names on
// the server's InviteCsvRow (ZBEdge.API.Client/Models/Admin/InviteCsvRow.cs). CSV mapping is by header NAME, not
// position (InviteCsvReader builds its header->key map from CsvColumnAttribute.ColumnName, case-insensitive), so this
// list's ORDER is free to be the grid's display order — the serializer writes columns in this order and the endpoints
// still bind each by its header. Both the grid component and the CSV serializer consume this list, so there is exactly
// one place that defines the column contract.

export type InviteColumnType = 'text' | 'number' | 'date' | 'bool' | 'select';

export interface InviteColumn {
  // Property on InviteRow (the DataWeaver camelCase key the endpoints emit/consume).
  key: keyof InviteRow;
  // Exact CSV header text (the server's CsvColumn name) — used for serialization and server cell-error mapping.
  header: string;
  // Friendly, human-readable column heading shown in the grid (decoupled from the CSV header).
  label: string;
  // Editor rendered for the cell; also drives client-side value validation. 'select' is a pick-list cell (text when
  // idle, dropdown when focused) whose options the grid supplies per column (e.g. Source App from the apps lookup).
  type: InviteColumnType;
  // True when a per-row VALUE is required (mirrors the server's row-level rules), so the grid can highlight a
  // missing-but-required cell. Note the server marks every CSV column Required for HEADER presence; that is a
  // structural concern handled server-side and is unrelated to this per-value flag.
  required: boolean;
  // When true the column is NOT shown in the grid and is skipped by client validation, but it REMAINS in this
  // list so the CSV serializer still emits its header and value. The server's InviteCsvRow marks every column
  // Required for header PRESENCE, so a hidden column must keep round-tripping or :load fails with MALFORMED_CSV.
  // These are columns the bulk-load UI does not expose to the operator (set elsewhere or left at default).
  hidden?: boolean;
}

// Display order per MM-42243: # | Email | First Name | Last Name | Phone Number | Birthday | NPI | Prefix | Suffix |
// Language Code | Country Code | Source App | Account Type | the remaining editable columns | Source App User ID
// (last). Hidden columns (phone country code, migrating-source-app-user, invalidated, created-by ZBEdge id) are
// kept at the end of the list so the serializer still writes all 23 headers; they are filtered out of the grid.
export const InviteColumns: InviteColumn[] = [
  { key: 'email', header: 'Email', label: 'Email', type: 'text', required: true },
  { key: 'firstName', header: 'FirstName', label: 'First Name', type: 'text', required: true },
  { key: 'lastName', header: 'LastName', label: 'Last Name', type: 'text', required: true },
  { key: 'phone', header: 'Phone', label: 'Phone Number', type: 'text', required: false },
  { key: 'birthday', header: 'Birthday', label: 'Birthday', type: 'date', required: false },
  { key: 'npi', header: 'NPI', label: 'NPI', type: 'text', required: false },
  { key: 'prefix', header: 'Prefix', label: 'Prefix', type: 'text', required: false },
  { key: 'suffix', header: 'Suffix', label: 'Suffix', type: 'text', required: false },
  { key: 'languageCode', header: 'LanguageCode', label: 'Language Code', type: 'text', required: false },
  { key: 'countryCode', header: 'CountryCode', label: 'Country Code', type: 'text', required: false },
  // Not row-required: the Source App is supplied by the page's subsection Source App dropdown, which stamps the
  // selected app into every row. The cell remains an editable dropdown for per-row display/override.
  { key: 'sourceApp', header: 'SourceApp', label: 'Source App', type: 'select', required: false },
  { key: 'accountType', header: 'AccountType', label: 'Account Type', type: 'text', required: false },
  { key: 'clientName', header: 'ClientName', label: 'Client Name', type: 'text', required: false },
  { key: 'clientId', header: 'ClientId', label: 'Client ID', type: 'number', required: false },
  { key: 'forceEmailResend', header: 'ForceEmailResend', label: 'Force Email Resend', type: 'bool', required: false },
  { key: 'dontSendEmail', header: 'DontSendEmail', label: "Don't Send Email", type: 'bool', required: false },
  { key: 'isOptivuTenantUser', header: 'IsOptivuTenantUser', label: 'Is OptiVu Tenant User', type: 'bool', required: false },
  { key: 'alwaysRequire2Fa', header: 'AlwaysRequire2FA', label: 'Always Require 2FA', type: 'bool', required: false },
  { key: 'sourceAppUserId', header: 'SourceAppUserID', label: 'Source App User ID', type: 'number', required: false },
  // Hidden columns — not shown in the grid, still serialized (see `hidden` above). PhoneCountryCode is set by the
  // Phone Number cell's phone component (stores the dial extension); the rest are not exposed by the bulk-load UI.
  { key: 'phoneCountryCode', header: 'PhoneCountryCode', label: 'Phone Country Code', type: 'text', required: false, hidden: true },
  { key: 'migratingSourceAppUser', header: 'MigratingSourceAppUser', label: 'Migrating Source App User', type: 'bool', required: false, hidden: true },
  { key: 'invalidated', header: 'Invalidated', label: 'Invalidated', type: 'bool', required: false, hidden: true },
  { key: 'createdByZbedgeId', header: 'CreatedByZBEdgeId', label: 'Created By ZBEdge ID', type: 'text', required: false, hidden: true }
];

// The grid's displayed columns: the schema minus the hidden ones. Serialization and header mapping still use the
// full InviteColumns list, so hidden columns keep round-tripping through the CSV.
export const VisibleInviteColumns: InviteColumn[] = InviteColumns.filter(col => !col.hidden);

// CSV header -> column, for mapping a server cell error (keyed by header) back to a grid column/key.
export const InviteColumnByHeader: Record<string, InviteColumn> =
  InviteColumns.reduce((map, col) => {
    map[col.header] = col;
    return map;
  }, {} as Record<string, InviteColumn>);

// A fresh, empty grid row: text/number/date cells null, the five non-null bools false, nullable Invalidated false.
export function createBlankInviteRow(): InviteRow {
  return {
    email: null,
    sourceApp: null,
    sourceAppUserId: null,
    firstName: null,
    lastName: null,
    prefix: null,
    suffix: null,
    phone: null,
    phoneCountryCode: null,
    birthday: null,
    npi: null,
    clientName: null,
    clientId: null,
    languageCode: null,
    countryCode: null,
    accountType: null,
    migratingSourceAppUser: false,
    forceEmailResend: false,
    dontSendEmail: false,
    isOptivuTenantUser: false,
    alwaysRequire2Fa: false,
    invalidated: false,
    createdByZbedgeId: null
  };
}
